import { useMemo, useState } from "react";
import { useForm, useWatch } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { z } from "zod";

import { Drawer } from "@/components/ui/Modal.jsx";
import Button from "@/components/ui/Button.jsx";
import { Field, Input, Select, SwitchRow, Textarea } from "@/components/ui/Form.jsx";
import { Callout } from "@/components/ui/Display.jsx";
import Icon from "@/components/ui/Icon.jsx";
import ShiftTimeline from "./ShiftTimeline.jsx";
import { DAYS, DAY_LABEL, WORK_WEEK, describeShift, formatSpan } from "@/lib/schedule.js";
import { cx } from "@/lib/cx.js";

/*
 * Create / edit a tracking schedule.
 *
 *   create  POST  /api/tracking-schedules       { name, description, days,
 *                                                 startTime, endTime, timezone,
 *                                                 breakTimes, scheduleType, userId }
 *   edit    PATCH /api/tracking-schedules/:id   the same
 *
 * Net working time updates as you type, because that is the number a manager is
 * actually setting — and it is NOT the shift span: paid breaks stay inside the
 * target, unpaid ones come out. Subtracting all of them understates the day and
 * makes the whole team look behind.
 *
 * Overnight shifts are supported and called out. They used to be impossible to
 * save at all, and the split that tells the desktop when to track produced no
 * windows for them — see backend/utils/scheduleTime.js.
 */
const timeField = z
    .string()
    .regex(/^([0-1]?\d|2[0-3]):[0-5]\d$/, "Use HH:mm, e.g. 09:00");

const schema = z.object({
    name: z.string().trim().min(1, "Please enter a name"),
    description: z.string().trim().min(1, "Please enter a description"),
    startTime: timeField,
    endTime: timeField,
    timezone: z.string().trim().default("UTC+6"),
    isActive: z.boolean().default(true),
});

const BLANK_BREAK = { name: "", startTime: "", endTime: "", isPaid: false };

export default function ScheduleFormDrawer({ schedule, onClose, onSubmit, saving, error }) {
    const editing = Boolean(schedule?._id);

    const [days, setDays] = useState(() => schedule?.days ?? WORK_WEEK);
    const [breaks, setBreaks] = useState(() =>
        (schedule?.breakTimes ?? []).map((b) => ({
            name: b.name ?? "",
            startTime: b.startTime ?? "",
            endTime: b.endTime ?? "",
            isPaid: Boolean(b.isPaid),
        })),
    );
    const [dayError, setDayError] = useState("");

    const {
        register,
        handleSubmit,
        control,
        formState: { errors },
    } = useForm({
        resolver: zodResolver(schema),
        mode: "onSubmit",
        reValidateMode: "onChange",
        defaultValues: {
            name: schedule?.name ?? "",
            description: schedule?.description ?? "",
            startTime: schedule?.startTime ?? "09:00",
            endTime: schedule?.endTime ?? "17:00",
            timezone: schedule?.timezone ?? "UTC+6",
            isActive: schedule?.isActive ?? true,
        },
    });

    const startTime = useWatch({ control, name: "startTime" });
    const endTime = useWatch({ control, name: "endTime" });

    const shift = useMemo(
        () => describeShift(startTime, endTime, breaks),
        [startTime, endTime, breaks],
    );

    const toggleDay = (day) =>
        setDays((current) =>
            current.includes(day) ? current.filter((d) => d !== day) : [...current, day],
        );

    const setBreak = (index, patch) =>
        setBreaks((current) => current.map((b, i) => (i === index ? { ...b, ...patch } : b)));

    const submit = (values) => {
        if (!days.length) {
            setDayError("Pick at least one day — a schedule with none never tracks.");
            return;
        }
        setDayError("");

        onSubmit({
            ...values,
            days,
            breakTimes: breaks.filter((b) => b.name && b.startTime && b.endTime),
        });
    };

    return (
        <Drawer
            open
            onClose={onClose}
            title={editing ? "Edit schedule" : "New schedule"}
            subtitle={editing ? schedule.name : "When the desktop app should track"}
            size="lg"
            footer={
                <>
                    <Button onClick={onClose} disabled={saving}>
                        Cancel
                    </Button>
                    <Button
                        variant="primary"
                        type="submit"
                        form="schedule-form"
                        loading={saving}
                        data-save-schedule
                    >
                        {editing ? "Save changes" : "Create schedule"}
                    </Button>
                </>
            }
        >
            <form id="schedule-form" onSubmit={handleSubmit(submit)} noValidate className="space-y-4">
                {error && <Callout tone="danger">{error}</Callout>}

                <Field label="Name" htmlFor="s-name" error={errors.name?.message}>
                    <Input id="s-name" data-autofocus autoComplete="off" {...register("name")} />
                </Field>

                <Field label="Description" htmlFor="s-description" error={errors.description?.message}>
                    <Textarea id="s-description" rows={2} {...register("description")} />
                </Field>

                {/* ---- Days ---- */}
                <div>
                    <p className="mb-2 text-[11px] font-semibold tracking-widest text-slate-400 uppercase dark:text-slate-500">
                        Days
                    </p>
                    <div className="flex flex-wrap gap-1.5">
                        {DAYS.map((day) => (
                            <button
                                key={day}
                                type="button"
                                data-day={day}
                                aria-pressed={days.includes(day)}
                                onClick={() => toggleDay(day)}
                                className={cx(
                                    "rounded-md border px-2.5 py-1.5 text-[12.5px] font-medium transition",
                                    days.includes(day)
                                        ? "border-brand-500 bg-brand-600 text-white"
                                        : "border-slate-200 text-slate-600 hover:bg-slate-50 dark:border-slate-700 dark:text-slate-300 dark:hover:bg-slate-800",
                                )}
                            >
                                {DAY_LABEL[day]}
                            </button>
                        ))}
                    </div>
                    {dayError && (
                        <p className="mt-1.5 text-[12px] text-rose-600 dark:text-rose-400" data-day-error>
                            {dayError}
                        </p>
                    )}
                    <p className="mt-1.5 text-[12px] text-slate-400 dark:text-slate-500">
                        {shift.overnight
                            ? "A night shift is tagged to the day it STARTS — Monday 22:00 runs into Tuesday morning."
                            : "The days this shift runs."}
                    </p>
                </div>

                {/* ---- Times ---- */}
                <div className="grid grid-cols-1 gap-4 sm:grid-cols-3">
                    <Field label="Start" htmlFor="s-start" error={errors.startTime?.message}>
                        <Input id="s-start" type="time" {...register("startTime")} />
                    </Field>
                    <Field label="End" htmlFor="s-end" error={errors.endTime?.message}>
                        <Input id="s-end" type="time" {...register("endTime")} />
                    </Field>
                    <Field label="Timezone" htmlFor="s-timezone">
                        <Select id="s-timezone" {...register("timezone")}>
                            {["UTC+6", "UTC+5:30", "UTC", "UTC-5", "UTC-8"].map((tz) => (
                                <option key={tz} value={tz}>
                                    {tz}
                                </option>
                            ))}
                        </Select>
                    </Field>
                </div>

                {shift.overnight && (
                    <Callout tone="info" data-overnight-notice>
                        <strong className="font-semibold">This shift crosses midnight.</strong> It
                        runs {formatSpan(shift.span)} from {startTime} to {endTime} the next day.
                    </Callout>
                )}

                {shift.span === 0 && (
                    <Callout tone="danger" data-zero-notice>
                        Start and end are the same, so this shift has no length. Change one of them.
                    </Callout>
                )}

                {/* ---- Timeline + totals ---- */}
                {shift.span > 0 && (
                    <div className="rounded-lg border border-slate-200 p-3.5 dark:border-slate-800">
                        <ShiftTimeline
                            startTime={startTime}
                            endTime={endTime}
                            breakTimes={shift.breaks}
                            span={shift.span}
                        />

                        <dl className="mt-3 grid grid-cols-3 gap-3 text-center">
                            <div>
                                <dt className="text-[11px] text-slate-400 dark:text-slate-500">
                                    Shift span
                                </dt>
                                <dd className="font-mono text-[13px] font-semibold" data-span>
                                    {formatSpan(shift.span)}
                                </dd>
                            </div>
                            <div>
                                <dt className="text-[11px] text-slate-400 dark:text-slate-500">
                                    Unpaid breaks
                                </dt>
                                <dd className="font-mono text-[13px] font-semibold" data-unpaid>
                                    −{formatSpan(shift.unpaidMinutes)}
                                </dd>
                            </div>
                            <div>
                                <dt className="text-[11px] text-slate-400 dark:text-slate-500">
                                    Net working
                                </dt>
                                <dd
                                    className="font-mono text-[13px] font-semibold text-emerald-600 dark:text-emerald-400"
                                    data-net
                                >
                                    {formatSpan(shift.netMinutes)}
                                </dd>
                            </div>
                        </dl>

                        {shift.paidMinutes > 0 && (
                            <p className="mt-2 text-center text-[11.5px] text-slate-400 dark:text-slate-500">
                                {formatSpan(shift.paidMinutes)} of paid break stays inside the
                                target.
                            </p>
                        )}
                    </div>
                )}

                {/* ---- Breaks ---- */}
                <div>
                    <div className="mb-2 flex items-center gap-2">
                        <p className="text-[11px] font-semibold tracking-widest text-slate-400 uppercase dark:text-slate-500">
                            Breaks
                        </p>
                        <Button
                            variant="link"
                            className="ml-auto"
                            onClick={() => setBreaks((c) => [...c, { ...BLANK_BREAK }])}
                            data-add-break
                        >
                            Add break
                        </Button>
                    </div>

                    {breaks.length ? (
                        <div className="space-y-2">
                            {breaks.map((br, i) => {
                                const described = shift.breaks[i];
                                return (
                                    <div
                                        key={i}
                                        data-break-row={i}
                                        className="rounded-lg border border-slate-200 p-2.5 dark:border-slate-800"
                                    >
                                        <div className="flex flex-wrap items-center gap-2">
                                            <Input
                                                value={br.name}
                                                onChange={(e) => setBreak(i, { name: e.target.value })}
                                                placeholder="Lunch"
                                                aria-label={`Break ${i + 1} name`}
                                                className="w-32"
                                            />
                                            <Input
                                                type="time"
                                                value={br.startTime}
                                                onChange={(e) => setBreak(i, { startTime: e.target.value })}
                                                aria-label={`Break ${i + 1} start`}
                                                className="w-32"
                                            />
                                            <Input
                                                type="time"
                                                value={br.endTime}
                                                onChange={(e) => setBreak(i, { endTime: e.target.value })}
                                                aria-label={`Break ${i + 1} end`}
                                                className="w-32"
                                            />

                                            <label className="flex items-center gap-1.5 text-[12.5px]">
                                                <input
                                                    type="checkbox"
                                                    checked={br.isPaid}
                                                    onChange={(e) => setBreak(i, { isPaid: e.target.checked })}
                                                    className="size-4 rounded border-slate-300 text-brand-600 focus:ring-brand-500/30 dark:border-slate-600"
                                                    aria-label={`Break ${i + 1} is paid`}
                                                />
                                                Paid
                                            </label>

                                            <Button
                                                variant="icon"
                                                className="ml-auto text-rose-600 dark:text-rose-400"
                                                aria-label={`Remove break ${i + 1}`}
                                                onClick={() =>
                                                    setBreaks((c) => c.filter((_, j) => j !== i))
                                                }
                                                data-remove-break={i}
                                            >
                                                <Icon name="xCircle" className="size-4" />
                                            </Button>
                                        </div>

                                        {described?.invalid && br.startTime && br.endTime && (
                                            <p
                                                className="mt-1.5 text-[12px] text-rose-600 dark:text-rose-400"
                                                data-break-error={i}
                                            >
                                                This break falls outside the shift — the server will
                                                refuse it.
                                            </p>
                                        )}

                                        {described && !described.invalid && described.length > 0 && (
                                            <p className="mt-1.5 text-[11.5px] text-slate-400 dark:text-slate-500">
                                                {formatSpan(described.length)}{" "}
                                                {br.isPaid ? "· paid, counts as work" : "· unpaid, comes off the target"}
                                            </p>
                                        )}
                                    </div>
                                );
                            })}
                        </div>
                    ) : (
                        <p className="text-[13px] text-slate-400 dark:text-slate-500">
                            No breaks. The whole shift is tracked.
                        </p>
                    )}
                </div>

                <SwitchRow
                    label="Active"
                    hint="An inactive schedule is ignored — the desktop then tracks whenever the app is running."
                    {...register("isActive")}
                />

                {editing && schedule.scheduleType === "individual" && (
                    <Callout tone="info">
                        This is an individual schedule and applies to one person only. It overrides
                        the organization's global schedule for them.
                    </Callout>
                )}
            </form>
        </Drawer>
    );
}
